// src/api/apiClient.ts
import { pickAuthToken } from '../utils/pickAuthToken';
// If VITE_API_BASE_URL is provided, use it; otherwise in development leave base empty
// so Vite's dev server proxy (configured in vite.config.ts) forwards /api to the backend.
const explicitBase = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
let API_BASE = '';
try {
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '';
  if (explicitBase) {
    const expHost = (() => { try { return new URL(explicitBase).host; } catch { return ''; } })();
    const curHost = (() => { try { return new URL(currentOrigin || 'http://localhost').host; } catch { return ''; } })();
    // Only use explicitBase when hosts match (same deployment); else prefer same-origin
    API_BASE = (!curHost || expHost === curHost) ? explicitBase : '';
  } else {
    API_BASE = '';
  }
} catch {
  API_BASE = explicitBase || '';
}

type FetchOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  auth?: boolean; // default true; when false, do not attach Authorization
};

function buildAuthHeaders(path: string, options: FetchOptions = {}): Record<string, string> {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const shouldAuth = options.auth !== false;
  let mergedHeaders: Record<string, string> = isFormData
    ? { ...(options.headers || {}) }
    : { 'Content-Type': 'application/json', ...(options.headers || {}) };

  if (shouldAuth && typeof window !== 'undefined') {
    const token = pickAuthToken(path);
    if (token && !mergedHeaders['authorization']) mergedHeaders['authorization'] = `Bearer ${token}`;
    if (!token && !mergedHeaders['authorization']) {
      try {
        const sess = JSON.parse(localStorage.getItem('owner_session') || '{}');
        if (sess?.id) mergedHeaders['x-owner-id'] = sess.id;
      } catch {}
    }
    const isDev = typeof import.meta !== 'undefined' && (import.meta as any).env && (import.meta as any).env.DEV;
    if (isDev && !mergedHeaders['authorization']) {
      const role = localStorage.getItem('dev_admin_role') || '';
      if (role && !mergedHeaders['x-user-role']) mergedHeaders['x-user-role'] = role;
    }
    const isPublic = /^\/api\/(public|templates|homepage|site|pricing|crm|auth)/.test(path);
    if (!token && shouldAuth && !isPublic && !mergedHeaders['x-owner-id']) {
      const err: any = new Error('Skipped unauthenticated request');
      err.status = 401;
      err.url = path;
      throw err;
    }
  }
  return mergedHeaders;
}

export async function fetchBlob(path: string, options: FetchOptions = {}): Promise<Blob> {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const mergedHeaders = buildAuthHeaders(path, options);
  const method = (options.method || 'GET').toUpperCase();
  if (method === 'GET' && mergedHeaders['Content-Type'] === 'application/json') {
    delete mergedHeaders['Content-Type'];
  }
  const { headers: _optsHeaders, auth: _optsAuth, ...fetchInit } = options;
  const res = await fetch(url, {
    ...fetchInit,
    credentials: 'include',
    headers: mergedHeaders,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let data: any;
    try { data = text ? JSON.parse(text) : undefined; } catch { data = undefined; }
    const err: any = new Error((data?.error || data?.message || text) || `Request failed: ${res.status}`);
    err.status = res.status;
    if (data !== undefined) err.data = data;
    err.url = url;
    throw err;
  }
  return res.blob();
}

export async function fetchJson<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const mergedHeaders = buildAuthHeaders(path, options);
  const { headers: _optsHeaders, auth: _optsAuth, ...fetchInit } = options;

  const res = await fetch(url, {
    ...fetchInit,
    credentials: 'include',
    headers: mergedHeaders,
  });

  if (!res.ok) {
  const text = await res.text().catch(() => '');
  let data: any = undefined;
  try { data = text ? JSON.parse(text) : undefined; } catch { data = undefined; }
  const err: any = new Error((data?.error || data?.message || text) || `Request failed: ${res.status}`);
  err.status = res.status;
  if (data !== undefined) err.data = data;
  err.url = url;
  throw err;
  }
  // Handle no-content responses gracefully (e.g., DELETE 204)
  if (res.status === 204 || res.status === 205) {
    return undefined as unknown as T;
  }

  // Robust parse: try text first; if empty, return undefined; else parse JSON
  const okText = await res.text().catch(() => '');
  if (!okText) return undefined as unknown as T;
  try {
    return JSON.parse(okText) as T;
  } catch {
    // If it's not JSON, return as-any string
    return okText as unknown as T;
  }
}
